// src/components/ContactsPage.jsx
// Global contacts view.
// Mode 1 — Quick Search: search by name or company
// Mode 2 — Advanced Search: filter by title, state, core system, industry, tier etc
//           Uses /api/contacts/advanced-search (two-step account->contact join)
//           UI ready for post-migration SQL upgrade — no UI changes needed then.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
// P0.31 31.B.5 — the Archived view. 24 contacts were archived and unreachable.
import useArchivedView from '../hooks/useArchivedView';
import './ContactsPage.css';
import DataTransferMenu from './DataTransferMenu';
import useTenantVocabulary from '../hooks/useTenantVocabulary';
import ContactsTab from './ContactsTab';   // R3 — standalone mode renders just the modal

// FALLBACKS ONLY. These were the page's filter vocabularies, hardcoded:
// Guidewire / Duck Creek / Majesco / Sapiens as "core systems", and P&C, Life &
// Annuity, Reinsurance, MGA/MGU as "industries". They are insurance policy-admin
// vendors and insurance lines — for any other tenant, a filter set that CAN NEVER
// MATCH A ROW, presented as the product's idea of their market. Same defect as
// exavalu_relevance, renamed in migration 0030 because nothing in the base may
// carry one customer's brand.
//
// The options now come from /api/tenant/vocabulary, derived from the
// tenant's OWN rows, so a filter cannot offer a value that matches nothing. These
// arrays remain only as the shape a select expects while that request is in
// flight, and are never shown once it answers — including when it answers [].
const FALLBACK_TITLE_OPTIONS = [
  'CIO', 'CTO', 'CFO', 'CEO', 'COO',
  'VP Technology', 'VP Operations', 'Director of IT', 'Director of Operations',
  'President', 'Managing Director', 'Project Manager', 'Consultant',
];

// P0.21 task 21.6c — relationship shapes, mirroring VOCAB.accountType.
const ACCOUNT_TYPE_OPTIONS = ['Customer', 'Competitor', 'Vendor', 'Partner'];
const TIER_OPTIONS         = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'];
const RATING_OPTIONS       = ['Hot', 'Warm', 'Cold'];
const STATUS_OPTIONS       = [
  'Active', 'Engaged', 'Targeted', 'Under Review', 'Stalled',
  'Closed Won', 'Closed Tabled by Client', 'Closed Lost to Competitor',
];

const TYPE_COLORS = {
  'Customer':        { bg: 'var(--dm-purple-light)', color: '#6d28d9' },
  'Competitor':      { bg: 'var(--dm-blue-light)', color: 'var(--dm-blue-deep)' },
  'Vendor':          { bg: 'var(--dm-amber-light)', color: 'var(--dm-amber-deep)' },
  'Partner':         { bg: 'var(--dm-green-wash)', color: '#15803d' },
};

// Relationship status → left border color + pill style
const RELATIONSHIP_STYLE = {
  'Strong  Relationship': { border: '#16a34a', bg: 'var(--dm-green-wash)', color: '#15803d', label: 'Strong Relationship' },
  'Engaged Positve':      { border: 'var(--dm-blue-vivid)', bg: 'var(--dm-blue-light)', color: 'var(--dm-blue-deep)', label: 'Engaged' },
  'Customer':             { border: 'var(--dm-indigo)', bg: 'var(--dm-purple-light)', color: '#6d28d9', label: 'Customer' },
  'Negative':             { border: 'var(--dm-red)', bg: 'var(--dm-red-light)', color: 'var(--dm-red-deep)', label: 'Negative' },
  'None':                 { border: 'var(--dm-grey-400)', bg: '#f3f4f6', color: 'var(--dm-grey-600)', label: 'No Relationship' },
};

function getRelationshipStyle(relationship) {
  if (!relationship || relationship.length === 0) return null;
  // Priority order: Strong > Engaged > Customer > Negative > None
  const priority = ['Strong  Relationship', 'Engaged Positve', 'Customer', 'Negative', 'None'];
  const found = priority.find(r => relationship.includes(r));
  return found ? RELATIONSHIP_STYLE[found] : null;
}

function ContactCard({ contact, onNavigate, onOpen }) {
  const typeKey   = contact.contact_type || contact._dbType || '';
  const typeColor = TYPE_COLORS[typeKey] || { bg: 'var(--dm-grey-200)', color: 'var(--dm-grey-700)' };
  const relStyle  = getRelationshipStyle(contact.relationship || []);
  const [enriching,  setEnriching]  = React.useState(false);
  const [enriched,   setEnriched]   = React.useState(false);
  // A failed enrich, shown rather than swallowed (their 148.4).
  const [enrichError, setEnrichError] = React.useState(null);
  const [enrichData, setEnrichData] = React.useState(null);

  const handleEnrich = async (e) => {
    e.stopPropagation();
    if (!contact.id || enriching || enriched) return;
    setEnriching(true);
    setEnrichError(null);
    try {
      const res  = await apiFetch('/api/contacts/enrich', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contactId: contact.id }),
      });
      const data = await res.json().catch(() => ({}));
      // res.ok, not just data.success. apiFetch RESOLVES on 4xx/5xx, so the
      // catch below never fires for an HTTP error and `data.success` is merely
      // undefined — the spinner cleared and nothing told the user the enrich
      // had failed. A 404 on this endpoint looked identical to "no new data".
      if (!res.ok || data.success === false) {
        setEnrichError(data.error || `Enrich failed (${res.status})`);
        return;
      }
      setEnriched(true);
      setEnrichData(data.enriched);
    } catch (err) {
      setEnrichError(err.message || 'Enrich failed');
    }
    finally { setEnriching(false); }
  };

  const displayEmail = enrichData?.email || contact.email;
  const displayPhone = enrichData?.phone || enrichData?.mobile_phone || contact.phone || contact.mobile_phone;

  return (
    <div className="cp-card"
      // R3 — OPEN THE CONTACT. This used to navigate to the contact's ACCOUNT,
      // and did nothing at all when there was no account:
      //
      //   const destId = contact.accountId || …;
      //   if (destId && onNavigate) onNavigate('account-detail', destId);
      //
      // So every account-less contact was a dead card — no route to view or edit
      // it from anywhere in the app, because the only contact editor lives in the
      // account's Contacts tab. One tenant had 1,164 of them from a LinkedIn
      // import and reported the page as broken. It was not broken; it had no
      // destination.
      //
      // The card now opens the contact itself, which is what a card of a thing
      // should do. Navigating to the account is still available from inside the
      // modal, where it reads as a choice rather than the only behaviour.
      onClick={() => onOpen && onOpen(contact)}
      style={{ cursor: 'pointer',
        borderLeft: relStyle ? '4px solid ' + relStyle.border : '0.5px solid #e5e7eb',
      }}>

      <div className="cp-card-header">
        <div className="cp-card-avatar"
          style={relStyle ? { background: relStyle.bg, color: relStyle.color } : {}}>
          {(contact.name || '?').charAt(0).toUpperCase()}
        </div>
        <div className="cp-card-name-block">
          <div className="cp-card-name">{contact.name}</div>
          {contact.title && <div className="cp-card-title">{contact.title}</div>}
        </div>
      </div>

      {contact.accountName && (
        <div className="cp-card-company-row">
          <span className="cp-card-company-pill" style={{ background: typeColor.bg, color: typeColor.color }}>
            {contact.accountName}
          </span>
        </div>
      )}

      <div className="cp-card-details">
        {contact.department && (
          <div className="cp-card-detail">
            <span className="cp-detail-label">Dept</span>
            <span className="cp-detail-value">{contact.department}</span>
          </div>
        )}
        {contact.influence_level && (
          <div className="cp-card-detail">
            <span className="cp-detail-label">Influence</span>
            <span className="cp-detail-value">{contact.influence_level}</span>
          </div>
        )}
        {contact.email || enrichData?.email ? (
          <div className="cp-card-detail">
            <span className="cp-detail-label">Email</span>
            <a href={'mailto:' + displayEmail} className="cp-detail-link"
              onClick={e => e.stopPropagation()}>{displayEmail}</a>
          </div>
        ) : null}
        {(contact.phone || contact.mobile_phone || enrichData?.phone || enrichData?.mobile_phone) ? (
          <div className="cp-card-detail">
            <span className="cp-detail-label">Phone</span>
            <span className="cp-detail-value">{displayPhone}</span>
          </div>
        ) : null}
      </div>

      <div className="cp-card-footer">
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="cp-type-badge" style={{ background: typeColor.bg, color: typeColor.color }}>
            {typeKey}
          </span>
          {relStyle && relStyle.label !== 'No Relationship' && (
            <span className="cp-rel-badge" style={{ background: relStyle.bg, color: relStyle.color }}>
              {relStyle.label}
            </span>
          )}
        </div>
        {contact.linkedin || enrichData?.linkedin_url ? (
          <a href={contact.linkedin || enrichData?.linkedin_url} target="_blank" rel="noreferrer"
            className="cp-linkedin-link" onClick={e => e.stopPropagation()}>in</a>
        ) : null}
        {!enriched && !contact.zoominfo_id && (
          <button className="cp-enrich-btn" onClick={handleEnrich} disabled={enriching}
            title={enrichError || 'Enrich with ZoomInfo'}>
            {enriching ? '⏳' : enrichError ? '⚠ ZI' : '⚡ ZI'}
          </button>
        )}
        {enrichError && (
          /* Inline and terse: this sits in a dense contact row, so the detail
             goes in the button's title and the row only shows that it failed.
             Previously the spinner just stopped and nothing said anything. */
          <span className="cp-enrich-error" title={enrichError}>enrich failed</span>
        )}
        {enriched && (
          <span className="cp-enriched-badge" title={'ZoomInfo: ' + (enrichData?.contact_accuracy_score ? enrichData.contact_accuracy_score + '% accuracy' : 'enriched')}>
            ✓ ZI
          </span>
        )}
      </div>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, placeholder }) {
  return (
    <div className="cp-filter-field">
      <label className="cp-filter-label">{label}</label>
      <select className="cp-filter-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">{placeholder || 'Any'}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

export default function ContactsPage({ onNavigate }) {
  // R3 — the contact currently open in the modal. Holds an ID ONLY: ContactsTab's
  // standalone mode re-fetches the full row from /api/contacts/get, because a
  // search result carries 12 of the 25 fields the editor needs and handleSave
  // writes all of its properties unconditionally — seeding the form from a search
  // result would blank `notes` and `mobile_phone` on save.
  const [openContactId, setOpenContactId] = useState(null);

  const [mode,       setMode]       = useState('quick');
  const [quickQuery, setQuickQuery] = useState('');
  const [quickMode,  setQuickMode]  = useState('name');
  const [filters,    setFilters]    = useState({
    title: '', state: '', coreSystem: '', industry: '',
    accountType: '', tier: '', rating: '', accountStatus: '',
  });
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [searched, setSearched] = useState(false);
  const debounceRef = useRef(null);

  // ── Browse ────────────────────────────────────────────────────────────────
  // This page had NO initial load: two search boxes and an empty body, so a
  // contact was reachable only by typing a name you already knew. The contacts
  // that most needed finding were the least findable — advanced-search filters
  // `.in('account_id', …)`, so an unlinked contact could not be returned by it at
  // all. One tenant had 1,164 invisible that way.
  const [browse,        setBrowse]        = useState([]);
  const [browseTotal,   setBrowseTotal]   = useState(0);
  const [unlinkedTotal, setUnlinkedTotal] = useState(0);
  const [browseLoading, setBrowseLoading] = useState(true);
  const [onlyUnlinked,  setOnlyUnlinked]  = useState(false);
  const BROWSE_LIMIT = 50;

  // Filter vocabularies, derived from this tenant's rows. `null` means "not
  // answered yet" and is distinct from `[]`, which means the tenant genuinely has
  // no values for that column — in which case the filter is HIDDEN rather than
  // shown empty, since a control that can only return zero rows is worse than no
  // control.
  // Option lists derived from this tenant's own rows. See useTenantVocabulary:
  // null means not answered yet, [] means the tenant genuinely has no values.
  const filterOptions = useTenantVocabulary();

  // Load the list, and the unlinked count alongside it. Two requests rather than
  // one because the count must be of ALL unlinked contacts, not of the fifty on
  // screen — "1,164 not linked" is the number that makes the state visible, and a
  // page-local count would say "50" and mean nothing.
  const archive = useArchivedView();

  const loadBrowse = useCallback(async () => {
    setBrowseLoading(true);
    try {
      const url = `/api/contacts/list?limit=${BROWSE_LIMIT}` + (onlyUnlinked ? '&unlinked=1' : '') + archive.param;
      const [listRes, unlinkedRes] = await Promise.all([
        apiFetch(url),
        // limit=1: this call is for `total`, not for rows.
        apiFetch('/api/contacts/list?limit=1&unlinked=1'),
      ]);
      const list = await listRes.json().catch(() => ({}));
      const unl  = await unlinkedRes.json().catch(() => ({}));

      // res.ok, not data.success alone — apiFetch RESOLVES on 4xx/5xx, so checking
      // the body only would render an empty list as though the tenant had none.
      if (listRes.ok && list.success) {
        setBrowse(list.contacts || []);
        setBrowseTotal(list.total || 0);
      }
      if (unlinkedRes.ok && unl.success) setUnlinkedTotal(unl.total || 0);
    } catch {
      // A failed browse is not worth an error banner over the search UI, which
      // still works. The empty state below says the list could not be loaded.
      setBrowse([]);
    } finally {
      setBrowseLoading(false);
    }
  }, [onlyUnlinked, archive.param]);

  useEffect(() => { loadBrowse(); }, [loadBrowse]);


  const doQuickSearch = useCallback(async (q, qMode) => {
    if (!q || q.trim().length < 2) { setContacts([]); setSearched(false); return; }
    setLoading(true); setError(null);
    try {
      const res  = await apiFetch('/api/contacts/search?q=' + encodeURIComponent(q.trim()) + '&mode=' + qMode);
      const data = await res.json();
      if (data.success) { setContacts(data.contacts || []); setSearched(true); }
      else { setError(data.error || 'Search failed'); setContacts([]); }
    } catch { setError('Network error'); setContacts([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (mode !== 'quick') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doQuickSearch(quickQuery, quickMode), 400);
    return () => clearTimeout(debounceRef.current);
  }, [quickQuery, quickMode, mode, doQuickSearch]);

  const doAdvancedSearch = useCallback(async () => {
    const hasFilter = Object.values(filters).some(v => v.trim() !== '');
    if (!hasFilter) { setError('Set at least one filter to search'); return; }
    setLoading(true); setError(null); setSearched(false);
    try {
      const res  = await apiFetch('/api/contacts/advanced-search', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(filters),
      });
      const data = await res.json();
      if (data.success) {
        setContacts(data.contacts || []);
        setAccounts(data.accounts || []);
        setSearched(true);
      } else { setError(data.error || 'Search failed'); setContacts([]); }
    } catch { setError('Network error'); setContacts([]); }
    finally { setLoading(false); }
  }, [filters]);

  const setFilter   = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const clearAll    = () => { setFilters({ title:'', state:'', coreSystem:'', industry:'', accountType:'', tier:'', rating:'', accountStatus:'' }); setContacts([]); setSearched(false); setError(null); };
  const switchMode  = (m) => { setMode(m); setContacts([]); setAccounts([]); setSearched(false); setError(null); };
  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="cp-global-root">
      <div className="cp-global-header">
        <div className="cp-global-header-left">
          <h1 className="cp-global-title">Contacts</h1>
          <p className="cp-global-subtitle">Search and filter across all contacts in Digital Me</p>
        </div>
        <div className="cp-global-header-right">
          {/* No onImported: this page is search-driven and has no list to refetch —
              results appear when the user runs a search. */}
          {/* onImported refetches the browse list after a commit, so imported
              contacts appear without a reload. Contacts was the ONE page not
              passing this — LeadsPage, AccountsListPage, PipelinePage and
              TasksPage all did — because until the browse list existed there was
              nothing here to refetch. Adding the list without this left an import
              that reported "3 imported" over a list still showing the old count,
              which reads as a failed import. */}
          <DataTransferMenu entity="contacts" label="Contacts" onImported={loadBrowse} />
        </div>
      </div>

      {/* Mode tabs */}
      <div className="cp-mode-tabs">
        <button className={'cp-mode-tab' + (mode === 'quick'    ? ' cp-mode-tab--active' : '')} onClick={() => switchMode('quick')}>Quick Search</button>
        <button className={'cp-mode-tab' + (mode === 'advanced' ? ' cp-mode-tab--active' : '')} onClick={() => switchMode('advanced')}>
          Advanced Filters{activeCount > 0 && <span className="cp-filter-badge">{activeCount}</span>}
        </button>
      </div>

      {/* Quick Search */}
      {mode === 'quick' && (
        <div className="cp-quick-panel">
          <div className="cp-global-toolbar">
            <div className="cp-mode-toggle">
              <button className={'cp-mode-btn' + (quickMode === 'name'    ? ' cp-mode-btn--active' : '')} onClick={() => setQuickMode('name')}>By Name</button>
              <button className={'cp-mode-btn' + (quickMode === 'company' ? ' cp-mode-btn--active' : '')} onClick={() => setQuickMode('company')}>By Company</button>
            </div>
            <input className="cp-global-search" type="text" autoFocus
              placeholder={quickMode === 'name' ? 'Search by contact name…' : 'Search by company name…'}
              value={quickQuery} onChange={e => setQuickQuery(e.target.value)} />
          </div>
        </div>
      )}

      {/* Advanced Search */}
      {mode === 'advanced' && (
        <div className="cp-advanced-panel">
          <p className="cp-advanced-note">
            Combine any filters below. Empty fields match everything.
            <span className="cp-advanced-note-future"> Post-migration these run as a single SQL query.</span>
          </p>

          <div className="cp-filter-grid">
            <div className="cp-filter-group">
              <div className="cp-filter-group-label">Contact</div>
              <FilterSelect label="Title / Role" value={filters.title} options={filterOptions?.title ?? FALLBACK_TITLE_OPTIONS} onChange={v => setFilter('title', v)} placeholder="Any title" />
            </div>
            <div className="cp-filter-group">
              <div className="cp-filter-group-label">Account</div>
              <FilterSelect label="Account Type"   value={filters.accountType}   options={ACCOUNT_TYPE_OPTIONS} onChange={v => setFilter('accountType', v)} />
              {(filterOptions?.industry?.length ?? 1) > 0 && (
                <FilterSelect label="Industry" value={filters.industry} options={filterOptions?.industry ?? []} onChange={v => setFilter('industry', v)} />
              )}
              <FilterSelect label="Tier"            value={filters.tier}           options={TIER_OPTIONS}         onChange={v => setFilter('tier', v)} />
            </div>
            <div className="cp-filter-group">
              <div className="cp-filter-group-label">Technology &amp; Location</div>
              {(filterOptions?.coreSystem?.length ?? 1) > 0 && (
                <FilterSelect label="Core System" value={filters.coreSystem} options={filterOptions?.coreSystem ?? []} onChange={v => setFilter('coreSystem', v)} />
              )}
              <div className="cp-filter-field">
                <label className="cp-filter-label">HQ State</label>
                <input className="cp-filter-input" type="text" placeholder="e.g. OH, NY, TX"
                  value={filters.state} onChange={e => setFilter('state', e.target.value)} />
              </div>
            </div>
            <div className="cp-filter-group">
              <div className="cp-filter-group-label">Relationship</div>
              <FilterSelect label="Rating"         value={filters.rating}         options={RATING_OPTIONS}   onChange={v => setFilter('rating', v)} />
              <FilterSelect label="Account Status" value={filters.accountStatus}  options={STATUS_OPTIONS}   onChange={v => setFilter('accountStatus', v)} />
            </div>
          </div>

          <div className="cp-advanced-actions">
            <button className="cp-search-btn" onClick={doAdvancedSearch} disabled={loading}>
              {loading ? 'Searching…' : 'Search'}
            </button>
            {activeCount > 0 && (
              <button className="cp-clear-btn" onClick={clearAll}>Clear all</button>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      <div className="cp-results-area">
        {loading && <div className="cp-state-msg">Searching…</div>}
        {!loading && error   && <div className="cp-state-msg cp-state-error">{error}</div>}
        {/* BROWSE — shown until a search replaces it. This page used to render
            "Type at least 2 characters to search" over an empty body, which meant
            a contact could only be found by someone who already knew its name.
            The list is the default state now; search narrows it. */}
        {!loading && !searched && (
          <>
            <div className="cp-browse-meta">
              <span className="cp-results-count">
                {browseLoading ? 'Loading contacts…'
                  : `${browseTotal} contact${browseTotal !== 1 ? 's' : ''}`}
              </span>
              {/* The number that makes the invisible state visible. An unlinked
                  contact is skipped by research, signals and sequences, and no
                  other view in the app can surface one. */}
              {!browseLoading && unlinkedTotal > 0 && (
                <>
                  <span className="cp-browse-sep">·</span>
                  <button
                    type="button"
                    className={'cp-unlinked-toggle' + (onlyUnlinked ? ' cp-unlinked-toggle--on' : '')}
                    onClick={() => setOnlyUnlinked(v => !v)}
                    title="Contacts with no account are skipped by research, signals and sequences"
                  >
                    {onlyUnlinked
                      ? `Showing ${unlinkedTotal} not linked to an account — show all`
                      : `${unlinkedTotal} not linked to an account`}
                  </button>
                </>
              )}
              {/* P0.31 31.B.5 — the Archived view. Reuses the unlinked toggle's own
                  pattern rather than introducing a second kind of control on the
                  same line. 24 contacts were archived and reachable from nowhere. */}
              {!browseLoading && (
                <>
                  <span className="cp-browse-sep">·</span>
                  <button
                    type="button"
                    className={'cp-unlinked-toggle' + (archive.showArchived ? ' cp-unlinked-toggle--on' : '')}
                    onClick={archive.toggle}
                    title={archive.button.title}
                  >
                    {archive.showArchived ? 'Showing archived — show active' : 'Archived'}
                  </button>
                </>
              )}
              {!browseLoading && browseTotal > browse.length && (
                <span className="cp-browse-more"> · showing first {browse.length}</span>
              )}
            </div>

            {!browseLoading && browse.length === 0 && (
              <div className="cp-state-msg">
                {onlyUnlinked ? 'Every contact is linked to an account.' : 'No contacts yet.'}
              </div>
            )}

            {browse.length > 0 && (
              <div className="cp-cards-grid">
                {browse.map(contact => (
                  <ContactCard key={contact.id} contact={contact} onNavigate={onNavigate}
                    onOpen={c => setOpenContactId(c.id)} />
                ))}
              </div>
            )}
          </>
        )}
        {!loading && !error && searched && contacts.length === 0 && <div className="cp-state-msg">No contacts found matching your criteria</div>}
        {!loading && searched && contacts.length > 0 && (
          <>
            <div className="cp-results-meta">
              <span className="cp-results-count">{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</span>
              {mode === 'advanced' && accounts.length > 0 && (
                <span className="cp-results-accounts"> across {accounts.length} account{accounts.length !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="cp-cards-grid">
              {contacts.map(contact => (
                <ContactCard key={contact.id} contact={contact} onNavigate={onNavigate}
                  onOpen={c => setOpenContactId(c.id)} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* R3 — the contact editor, shared with the account's Contacts tab rather
          than reimplemented here. ContactsTab in standalone mode renders only the
          modal: it fetches the full contact by id, and calls onClose when
          dismissed. Keyed on the id so opening a second contact remounts rather
          than showing the first one's data while the fetch is in flight. */}
      {openContactId && (
        <ContactsTab
          key={openContactId}
          contactId={openContactId}
          onClose={() => {
            setOpenContactId(null);
            // Re-read on close. The modal can LINK a contact to an account, which
            // changes both the card and the "not linked" count — leaving them
            // stale would tell the user their fix had not worked.
            loadBrowse();
          }}
        />
      )}
    </div>
  );
}
